import * as React from 'react';
import { BodyText, TypographyProps } from '@minecraft.atoms';
import UncontrolledDropdown from '@minecraft.uncontrolled-dropdown';
import Button, { ButtonProps } from '@minecraft.button';
import DropdownMenu from '@minecraft.dropdown-menu';
import DropdownItem from '@minecraft.dropdown-item';
import DropdownToggle from '@minecraft.dropdown-toggle';
import { WindowWidthContext } from '@minecraft.utils';

interface OptionalProps {
  className?: string;
  qaId?: string;
  /** @deprecated: use 'data-testid' prop instead */
  testId?: string;
  'data-testid'?: string;
}

export interface DropdownItemProps extends OptionalProps {
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  label?: string;
  disabled?: boolean;
  active?: boolean;
  divider?: boolean;
  header?: boolean;
  id: string;
  toggle?: boolean;
  color?: TypographyProps['color'];
}

const buildOptionalProps = ({
  testId: legacyTestIdProp,
  'data-testid': testIdProp,
  qaId,
  className,
}: OptionalProps) => {
  const testId = testIdProp || legacyTestIdProp;

  return {
    ...(typeof testId === 'string' && testId ? { 'data-testid': testId } : {}),
    ...(typeof qaId === 'string' && qaId ? { 'data-qa-id': qaId } : {}),
    ...(typeof className === 'string' && className ? { className } : {}),
  };
};

export interface DropdownButtonProps extends OptionalProps {
  toggleLabel: string;
  toggleProps: Partial<Omit<ButtonProps, 'children' | 'qaId' | 'testId'>>;
  menuItemsProps: DropdownItemProps[];
  menuAlign?: 'right' | 'left';
  block?: boolean;
  mobileBlock?: boolean;
}

export const DropdownButton = ({
  toggleLabel,
  menuAlign = 'right',
  toggleProps = {},
  menuItemsProps = [],
  block = false,
  mobileBlock = false,
  qaId,
  testId,
  'data-testid': dataTestId,
  className,
}: DropdownButtonProps) => {
  const isMobile = WindowWidthContext.useIsMobile();
  const menuItemsWithIds = menuItemsProps.filter(
    (mi) => ['number', 'string'].includes(typeof mi.id) && !!mi.id.toString()
  );
  const rightAlignProp = menuAlign === 'right' ? { right: true } : {};
  const ddBlockWidth = block || (mobileBlock && isMobile) ? { width: '100%' } : {};
  const menuWidth = mobileBlock && isMobile ? { width: '100%' } : {};
  const qaIdBase = qaId || 'dropdown';
  const testIdBase = dataTestId || testId || 'dropdown';
  const { qaId: toggleQaId, testId: toggleTestId, ...restToggleProps } = toggleProps;

  return (
    <UncontrolledDropdown
      style={{ ...ddBlockWidth }}
      {...buildOptionalProps({ testId: `${testIdBase}-wrapper`, qaId: `${qaIdBase}-wrapper`, className })}
    >
      <DropdownToggle
        fixedWidth={false}
        {...restToggleProps}
        {...buildOptionalProps({
          testId: toggleTestId || `${testIdBase}-toggle`,
          qaId: toggleQaId || `${qaIdBase}-toggle`,
        })}
        tag={Button}
        caret
      >
        {toggleLabel}
      </DropdownToggle>
      <DropdownMenu
        style={{ ...menuWidth, maxHeight: '50vh', overflowX: 'auto' }}
        {...rightAlignProp}
        {...buildOptionalProps({ testId: `${testIdBase}-menu`, qaId: `${qaIdBase}-menu` })}
      >
        {menuItemsWithIds.map(
          ({ id, label, qaId: menuQaId, testId: menuTestId, className: menuClassName, color, ...miProps }) => (
            <DropdownItem
              key={id}
              {...miProps}
              {...buildOptionalProps({ testId: menuTestId, qaId: menuQaId, className: menuClassName })}
            >
              <BodyText color={color || 'default'}>{label}</BodyText>
            </DropdownItem>
          )
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};
